import {Anchor} from "../core/Anchor";
import {encodeHtmlEntities} from "../core/AssetHelpers";
import {BitmapText} from "../core/BitmapText";
import {Container} from "../core/Container";
import {IPoint} from "../core/Geom";
import {Callback} from "../core/Reflection";
import {TextInput} from "../core/TextInput";
import {Assets} from "../data/Assets";
import {SlotsModel} from "../data/SlotsModel";
import {AnyStorySceneModel, OptionId, SlotId, StoryVOProvider} from "../data/StoryModel";
import {GameManager} from "../managers/GameManager";
import {TextBox} from "./TextBox";

export class AbstractStoryScene extends Container {

    public readonly gameManager = GameManager.instance;
    public readonly caption = this.addChild(new TextBox());
    private captionInput?: TextInput;
    private _slots?: SlotsModel;

    public getSceneCaption(scene: AnyStorySceneModel, isOptions: boolean) {
        let template: string | StoryVOProvider | undefined = scene.cc;
        if (!template) {
            if (typeof scene.vo === "object" && !Array.isArray(scene.vo)) {
                template = scene.vo;
            } else {
                return "";
            }
        }
        if (typeof template === "object") {
            template = (template as StoryVOProvider).getCC();
        }
        const ellipsis = "...";
        return template.replace(/\{([^}]*)option-(\w+)([^}]*)\}/ig, (substring, prefix: string, slotId: SlotId, suffix: string) => {
            if (isOptions && scene.options.some((it) => it.slotId === slotId)) {
                return ellipsis;
            }
            const optionId = this.getSelectedOption(slotId);
            if (optionId) {
                const option = this.gameManager.getOption(slotId, optionId);
                if (option && option.cc) {
                    return `<em>${encodeHtmlEntities(prefix + option.cc.toUpperCase() + suffix)}</em>`;
                } else {
                    return ellipsis;
                }
            } else {
                return ellipsis;
            }
        });
    }

    public setCaptionPosition(size: IPoint) {
        this.caption.y = size.y * 0.5;
    }

    public cloneSlots() {
        this._slots = GameManager.instance.cloneSelectedOptions();
    }

    public selectOption(slotId: SlotId, optionId: OptionId) {
        if (this._slots) {
            this._slots.selectOption(slotId, optionId);
        }
    }

    public replaceOption(slotId: SlotId, oldOption: OptionId, newOption: OptionId) {
        if (this._slots) {
            this._slots.replaceOption(slotId, oldOption, newOption);
        }
    }

    public getSelectedOption(slotId: SlotId) {
        return this._slots ? this._slots.getSelectedOption(slotId) : this.gameManager.getSelectedOption(slotId);
    }

    public isOptionSelected(optionId: OptionId, slotId?: SlotId) {
        return this._slots ? this._slots.isOptionSelected(optionId, slotId) : this.gameManager.isOptionSelected(optionId, slotId);
    }

    public startEditCaption(onStopEdit: Callback) {
        if (!this.captionInput) {
            const data = this.caption.getCaptionData();
            this.captionInput = this.addChild(new TextInput("", {
                font: {name: Assets.fontGrandstander.assetName, size: data.fontSize},
                tint: 0x000000,
                align: "center",
                lineHeight: data.lineHeight,
            }, data.anchor));
            this.captionInput.x = this.caption.x + data.x;
            this.captionInput.y = this.caption.y + data.y;
            this.captionInput.textAreaWidth = data.width;
            this.captionInput.textAreaHeight = data.height;
            this.captionInput.cursorDisplay = new BitmapText("|", {
                font: {name: Assets.fontGrandstander.assetName, size: data.fontSize},
                tint: 0x000000,
                align: "center",
                lineHeight: data.lineHeight,
            }, Anchor.bottomLeft);
            this.captionInput.pattern = /^[\sa-zA-Z0-9,.?!:;'"]*$/;
            this.captionInput.onStopEdit.add(onStopEdit);
        }
        this.captionInput.startEdit();
        this.caption.visible = false;
    }

    public stopEditCaption() {
        if (this.captionInput) {
            this.captionInput.stopEdit();
        }
        this.caption.visible = !this.captionInput || !(this.captionInput.text.replace(/\s+/, ""));
    }
}
